import React, { useState, useMemo } from 'react';
import { Button, LoadingSpinner, DateRangePicker } from 'components/common';
import PropTypes from 'prop-types';
import { FiFilter, FiX, FiChevronDown, FiChevronUp, FiSearch, FiRefreshCw } from 'react-icons/fi';
// ── Shared sub-components (same pattern as SalesFilterSidebar) ──────────────

const FilterSection = ({ title, count, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-hidden">
      <Button onClick={() => setOpen(o => !o)} variant="ghost">
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-sm tw-font-semibold tw-text-gray-700">{title}</span>
          {count > 0 && (
            <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-xl tw-bg-blue-500 tw-text-white tw-text-xs tw-font-bold">
              {count}
            </span>
          )}
        </div>
        {open
          ? <FiChevronUp size={13} className="tw-text-gray-400" />
          : <FiChevronDown size={13} className="tw-text-gray-400" />}
      </Button>
      {open && (
        <div className="tw-px-3 tw-py-2.5 tw-space-y-1 tw-max-h-56 tw-overflow-y-auto tw-bg-white">
          {children}
        </div>
      )}
    </div>
  );
};
FilterSection.propTypes = { title: PropTypes.string, count: PropTypes.number, children: PropTypes.node };

const CheckItem = ({ label, checked, onChange }) => (
  <label className="tw-flex tw-items-center tw-gap-2.5 tw-py-1.5 tw-px-2 tw-rounded hover:tw-bg-gray-50 tw-cursor-pointer tw-transition-colors">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="tw-w-4 tw-h-4 tw-rounded-sm tw-border-gray-300 tw-text-blue-600 tw-cursor-pointer focus:tw-ring-2 focus:tw-ring-blue-500"
    />
    <span className="tw-text-sm tw-text-gray-700 tw-select-none tw-flex-1 tw-leading-snug">{label}</span>
  </label>
);
CheckItem.propTypes = { label: PropTypes.string, checked: PropTypes.bool, onChange: PropTypes.func };

const SearchInput = ({ value, onChange, placeholder }) => (
  <div className="tw-relative tw-mb-2">
    <FiSearch className="tw-absolute tw-left-2.5 tw-top-2.5 tw-text-gray-400" size={13} />
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      className="tw-w-full tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange('')}
        className="tw-absolute tw-right-2 tw-top-2 tw-text-gray-400 hover:tw-text-gray-600 tw-bg-transparent tw-border-0 tw-p-0 tw-leading-none tw-cursor-pointer"
      >
        <FiX size={11} />
      </button>
    )}
  </div>
);
SearchInput.propTypes = { value: PropTypes.string, onChange: PropTypes.func, placeholder: PropTypes.string };

// ── Hierarchical sales unit filter (grouped by region) ─────────────────────

const buildGroups = (units, search) => {
  const q = search.toLowerCase();
  const groups = {};
  units.forEach(unit => {
    const groupKey = unit.region || unit.name;
    const label    = [unit.name, unit.description].filter(Boolean).join(' - ');
    if (!groups[groupKey]) groups[groupKey] = [];
    if (!q || label.toLowerCase().includes(q) || groupKey.toLowerCase().includes(q)) {
      groups[groupKey].push(unit);
    }
  });
  return Object.entries(groups)
    .filter(([, items]) => items.length > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([region, items]) => ({ region, units: items }));
};

const EMPTY_FILTERS = {
  sales_units:     [],
  statuses:        [],
  cse_incharges:   [],
  sales_incharges: [],
  overdue:         false,
  commitment_from: '',
  commitment_to:   '',
};

const WaiverFilterSidebar = ({ filterOptions, loading, filters, onApply, onReset }) => {
  const [local, setLocal]                   = useState({ ...EMPTY_FILTERS, ...filters });
  const [unitSearch, setUnitSearch]         = useState('');
  const [cseSearch, setCseSearch]           = useState('');
  const [inchargeSearch, setInchargeSearch] = useState('');

  const toggle = (key, value) => {
    setLocal(prev => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const toggleGroup = (ids) => {
    setLocal(prev => {
      const current = prev.sales_units || [];
      const allOn   = ids.every(id => current.includes(id));
      return {
        ...prev,
        sales_units: allOn ? current.filter(id => !ids.includes(id)) : [...new Set([...current, ...ids])],
      };
    });
  };

  const handleReset = () => {
    setLocal(EMPTY_FILTERS);
    setUnitSearch(''); setCseSearch(''); setInchargeSearch('');
    onReset();
  };

  const activeCount =
    (local.sales_units?.length     || 0) +
    (local.statuses?.length        || 0) +
    (local.cse_incharges?.length   || 0) +
    (local.sales_incharges?.length || 0) +
    (local.overdue ? 1 : 0) +
    (local.commitment_from || local.commitment_to ? 1 : 0);

  const opts       = filterOptions || {};
  const salesUnits = useMemo(() => opts.sales_units || [], [opts.sales_units]);
  const statuses   = opts.statuses        || [];
  const cses       = useMemo(() => opts.cse_incharges || [], [opts.cse_incharges]);
  const incharges  = useMemo(() => opts.sales_incharges || [], [opts.sales_incharges]);

  const unitGroups = useMemo(() => buildGroups(salesUnits, unitSearch), [salesUnits, unitSearch]);
  const filteredCses = useMemo(
    () => cses.filter(n => n.toLowerCase().includes(cseSearch.toLowerCase())),
    [cses, cseSearch]
  );
  const filteredIncharges = useMemo(
    () => incharges.filter(n => n.toLowerCase().includes(inchargeSearch.toLowerCase())),
    [incharges, inchargeSearch]
  );

  return (
    <div
      className="tw-bg-white tw-rounded-2xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden tw-flex tw-flex-col"
      // Must match the sticky offset (navbar + dashboard header) so the
      // Apply Filters footer always stays inside the viewport
      style={{ height: 'max(320px, calc(100dvh - var(--falcon-top-nav-height, 69px) - 96px))' }}
    >
      {/* Header */}
      <div className="tw-px-4 tw-py-3.5 tw-bg-gradient-to-r tw-from-violet-600 tw-to-primary-700 tw-text-white tw-flex tw-items-center tw-justify-between tw-flex-shrink-0">
        <div className="tw-flex tw-items-center tw-gap-2">
          <FiFilter size={15} />
          <span className="tw-font-semibold tw-text-sm">Filters</span>
          {activeCount > 0 && (
            <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-xl tw-bg-white tw-text-primary-700 tw-text-xs tw-font-bold">
              {activeCount}
            </span>
          )}
        </div>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={handleReset}
            className="tw-text-xs tw-text-primary-100 hover:tw-text-white tw-flex tw-items-center tw-gap-1 tw-cursor-pointer tw-bg-transparent tw-border-0 tw-p-0 tw-leading-none"
          >
            <FiRefreshCw size={11} /> Reset
          </button>
        )}
      </div>

      {loading ? (
        <div className="tw-flex tw-justify-center tw-py-8 tw-flex-1">
          <LoadingSpinner size="sm" />
        </div>
      ) : (
        <div className="tw-p-3 tw-space-y-2 tw-flex-1 tw-overflow-y-auto">
          {/* Sales Units — hierarchical with region grouping */}
          <FilterSection title="Sales Unit" count={local.sales_units?.length || 0}>
            {salesUnits.length > 0 ? (
              <>
                <SearchInput value={unitSearch} onChange={setUnitSearch} placeholder="Search sales unit…" />
                {unitGroups.map(({ region, units: groupUnits }) => {
                  const ids    = groupUnits.map(u => u.id);
                  const allOn  = ids.every(id => (local.sales_units || []).includes(id));
                  const someOn = !allOn && ids.some(id => (local.sales_units || []).includes(id));
                  return (
                    <div key={region} className="tw-mb-1">
                      <label className="tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1 tw-rounded-md tw-bg-gray-50 tw-cursor-pointer hover:tw-bg-blue-50 tw-transition-colors">
                        <input
                          type="checkbox"
                          checked={allOn}
                          ref={el => { if (el) el.indeterminate = someOn; }}
                          onChange={() => toggleGroup(ids)}
                          className="tw-w-4 tw-h-4 tw-rounded-sm tw-border-gray-300 tw-text-blue-600 tw-cursor-pointer"
                        />
                        <span className="tw-text-xs tw-font-bold tw-text-blue-700 tw-uppercase tw-tracking-wide tw-select-none tw-flex-1">{region}</span>
                        <span className="tw-text-xs tw-text-gray-400">{ids.length}</span>
                      </label>
                      <div className="tw-pl-4 tw-mt-0.5 tw-space-y-0.5">
                        {groupUnits.map(unit => (
                          <CheckItem
                            key={unit.id}
                            label={[unit.name, unit.description].filter(Boolean).join(' - ')}
                            checked={(local.sales_units || []).includes(unit.id)}
                            onChange={() => toggle('sales_units', unit.id)}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
                {unitGroups.length === 0 && <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-2">No results</p>}
              </>
            ) : (
              <p className="tw-text-xs tw-text-gray-400 tw-italic tw-py-1">No sales units available</p>
            )}
          </FilterSection>

          {/* Waiver Status */}
          <FilterSection title="Waiver Status" count={local.statuses?.length || 0}>
            {statuses.length > 0 ? statuses.map(s => (
              <CheckItem
                key={s.id}
                label={s.name}
                checked={(local.statuses || []).includes(s.id)}
                onChange={() => toggle('statuses', s.id)}
              />
            )) : <p className="tw-text-xs tw-text-gray-400 tw-italic tw-py-1">No statuses available</p>}
          </FilterSection>

          {/* CSE Incharge */}
          <FilterSection title="CSE Incharge" count={local.cse_incharges?.length || 0}>
            {cses.length > 0 ? (
              <>
                <SearchInput value={cseSearch} onChange={setCseSearch} placeholder="Search CSE…" />
                {filteredCses.map(name => (
                  <CheckItem
                    key={name}
                    label={name}
                    checked={(local.cse_incharges || []).includes(name)}
                    onChange={() => toggle('cse_incharges', name)}
                  />
                ))}
                {filteredCses.length === 0 && <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-2">No results</p>}
              </>
            ) : (
              <p className="tw-text-xs tw-text-gray-400 tw-italic tw-py-1">No data available</p>
            )}
          </FilterSection>

          {/* Sales Incharge */}
          <FilterSection title="Sales Incharge" count={local.sales_incharges?.length || 0}>
            {incharges.length > 0 ? (
              <>
                <SearchInput value={inchargeSearch} onChange={setInchargeSearch} placeholder="Search incharge…" />
                {filteredIncharges.map(name => (
                  <CheckItem
                    key={name}
                    label={name}
                    checked={(local.sales_incharges || []).includes(name)}
                    onChange={() => toggle('sales_incharges', name)}
                  />
                ))}
                {filteredIncharges.length === 0 && <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-2">No results</p>}
              </>
            ) : (
              <p className="tw-text-xs tw-text-gray-400 tw-italic tw-py-1">No data available</p>
            )}
          </FilterSection>

          {/* Overdue */}
          <FilterSection title="Overdue" count={(local.overdue ? 1 : 0) + (local.commitment_from || local.commitment_to ? 1 : 0)}>
            <CheckItem
              label="Overdue only (approved, past commitment, not closed)"
              checked={!!local.overdue}
              onChange={() => setLocal(prev => ({ ...prev, overdue: !prev.overdue }))}
            />
            <div className="tw-pt-2 tw-space-y-1.5">
              <p className="tw-text-[10px] tw-font-bold tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mb-0">Commitment date range</p>
              {/* Portal-based dropdown — not clipped by the section's overflow scroll */}
              <DateRangePicker
                label="Commitment date"
                startDate={local.commitment_from || ''}
                endDate={local.commitment_to || ''}
                onChange={({ startDate, endDate }) =>
                  setLocal(prev => ({ ...prev, commitment_from: startDate || '', commitment_to: endDate || '' }))}
                className="tw-w-full"
              />
            </div>
          </FilterSection>
        </div>
      )}

      {/* Footer */}
      <div className="tw-px-3 tw-py-3 tw-border-t tw-border-gray-100 tw-flex-shrink-0" style={{ boxShadow: '0 -4px 6px -1px rgba(0,0,0,0.05)' }}>
        <Button
          onClick={() => onApply(local)}
          className="tw-w-full tw-justify-center tw-py-2 tw-text-sm tw-font-semibold tw-bg-primary-600 tw-text-white hover:tw-bg-primary-700 tw-rounded"
        >
          Apply Filters
        </Button>
      </div>
    </div>
  );
};

WaiverFilterSidebar.propTypes = {
  filterOptions: PropTypes.object,
  loading:       PropTypes.bool,
  filters:       PropTypes.object,
  onApply:       PropTypes.func.isRequired,
  onReset:       PropTypes.func.isRequired,
};
WaiverFilterSidebar.defaultProps = { filterOptions: null, loading: false, filters: {} };

export default WaiverFilterSidebar;
